import React, { useEffect, useMemo, useState } from "react";
import { Dialog, Box, Typography, IconButton, Chip, Button, Divider } from "@mui/material";
import RemoveCardDialog from "./RemoveCardDialog";
import CloseIcon from "@mui/icons-material/Close";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import CheckIcon from "@mui/icons-material/Check";
import AddIcon from "@mui/icons-material/Add";
import { getPokemonCardById } from "../../services/pokemonAPI";
import { getRarityBadge, getPriceDisplay, isFoilRarity } from "../../cardDisplay";
import CardImage from "./CardImage";
import HoloCard from "./HoloCard";
import Loader from "../../../styles/Loader";
import { useCurrency } from "../../../providers/currencyContext";
import { priceProvenance } from "../../../utils/currency";
import PriceHistoryChart from "./PriceHistoryChart";
import { useDismissible } from "../../../native/dismissibleStack";

const PLACEHOLDER_IMG = "https://images.pokemontcg.io/base1/1.png";

/** Grid rows already carry a slim card; only refetch when detail fields are absent. */
const hasDetailFields = (card) =>
  !!card && ("attacks" in card || "flavorText" in card || "weaknesses" in card);

const Section = ({ title, children }) => (
  <Box sx={{ mt: 2 }}>
    <Typography sx={{
      fontSize: 11, fontWeight: 800, color: "#64748b",
      textTransform: "uppercase", letterSpacing: "0.07em", mb: 0.8,
    }}>
      {title}
    </Typography>
    {children}
  </Box>
);

const MetaRow = ({ label, value }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, py: 0.5 }}>
    <Typography sx={{ fontSize: 13, color: "#64748b" }}>{label}</Typography>
    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0f172a", textAlign: "right" }}>
      {value}
    </Typography>
  </Box>
);

/**
 * Card detail sheet — the destination for a tap on any card tile.
 *
 * `card` is the slim grid row, shown immediately so the sheet never opens empty;
 * the full document is fetched behind it and merged in when it lands.
 */
const CardDetailModal = ({
  open,
  card,
  onClose,
  onToggleWishlist,
  onAddToCollection,
  isWishlisted,
  inCollection,
  // Photos the caller (Collection) took of *this specific card*. When present
  // they replace the stock catalog image — the sheet is showing "your card",
  // not "the card as it exists in the catalogue".
  ownerPhotos = [],
  // The grade of the specific copy this sheet stands for, when the caller has
  // one — the same prop the Collection tile already takes, for the same reason.
  // Omitted in the catalog, where there is no physical copy yet, and
  // getPriceDisplay then falls back to DEFAULT_CONDITION exactly as before.
  //
  // Without it the sheet re-priced the owner's Damaged copy at the Near Mint
  // reference, so tapping a tile changed the number for the same physical card
  // with nothing on screen saying the two figures meant different things.
  condition,
  // B-14: the copy is promised to an accepted trade; see RemoveCardDialog.
  promised = false,
}) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const cardId = card?._id;

  useEffect(() => {
    if (!open || !cardId) return undefined;

    // Reset so a previously-opened card's text never bleeds into this one.
    setDetail(null);
    setLoadError(false);

    if (hasDetailFields(card)) {
      setDetail(card);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    getPokemonCardById(cardId)
      .then((full) => {
        if (!cancelled) setDetail(full);
      })
      .catch(() => {
        if (!cancelled) setLoadError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, cardId, card]);

  const ownerUrls = useMemo(
    () => (ownerPhotos || []).map((p) => p?.url).filter(Boolean),
    [ownerPhotos]
  );
  const [activePhotoIdx, setActivePhotoIdx] = useState(0);
  // Is the primary image showing CardImage's fallback instead of the source we
  // handed it (#252)? Mirrored from CardImage rather than inferred here,
  // because only CardImage knows whether the picture actually decoded — and
  // mirrored as a live boolean rather than accumulated per URL, so that when
  // its answer changes back (a different photo selected, the sheet's contents
  // remounted) this follows instead of holding a stale verdict.
  const [primaryImageFellBack, setPrimaryImageFellBack] = useState(false);
  // If the caller's ownerPhotos changes (opening a different collection card),
  // reset to the first shot.
  const ownerUrlsKey = ownerUrls.join("|");
  useEffect(() => {
    setActivePhotoIdx(0);
  }, [ownerUrlsKey]);

  const { formatAmount } = useCurrency();
  // Price history is opt-in: it costs a request, and the card sheet should open
  // instantly rather than fetching a graph nobody asked for.
  const [historyOpen, setHistoryOpen] = useState(false);
  // #129 — see handleAddToCollection below.
  const [pendingRemove, setPendingRemove] = useState(false);
  // The sheet is reused: the parent keeps it mounted and swaps `open`/`card`.
  // Closing the sheet takes the confirm off screen with it (its render is
  // gated on `open` below) but leaves this flag set, so without the reset an
  // abandoned confirm would be back the moment the sheet reopens, now pointed
  // at a different card and one tap from deleting the wrong record.
  //
  // Native Back is no longer one of the routes into that state — it cancels
  // the confirm instead of closing the sheet: the confirm registers itself
  // (RemoveCardDialog), see the note below.
  //
  // Nor is anything else, today: while the confirm is up, the X in the corner
  // and the sheet's own backdrop are both behind the CONFIRM's full-viewport
  // backdrop, inside MUI's `aria-hidden` lower modal, so a tap there cancels
  // the confirm; and the only `open`-false writes either caller makes are the
  // `onClose` prop itself. So this reset currently guards a state nothing can
  // reach. It stays anyway, and not out of caution: the property it defends —
  // a confirm raised for card A must never be adopted by card B — must not
  // depend on that enumeration staying true when a third caller appears. The
  // #129 block in the test file drives it directly rather than through any
  // route, for the same reason.
  useEffect(() => {
    setPendingRemove(false);
  }, [open, cardId]);

  // Mirrors the render guard below: the sheet is only on screen with a card.
  useDismissible(Boolean(open && card), onClose, "card-detail-modal");

  // The remove confirm is a SECOND surface stacked over the sheet, and the
  // destructive one. It registers with the Back stack itself when it mounts
  // (RemoveCardDialog, label "card-detail-remove-confirm"), which is after the
  // sheet registered above, so LIFO hands Back to the confirm: one press
  // cancels the deletion and leaves this sheet on screen.

  if (!card) return null;

  // #129: exactly the P0-17 guard Card.jsx's badge already has. Both callers of
  // this sheet (CollectionPage, PokemonMain) hand `onAddToCollection` the raw
  // `toggleCollection`, which for a card already in the collection hard-deletes
  // the UserCard record — photos, condition grade, asking price and notes go
  // with it, server-side, with no undo. Never fire that from one tap on what
  // reads as a status badge. Adds (inCollection=false) still go straight
  // through — no dialog for the safe direction.
  const handleAddToCollection = () => {
    if (!onAddToCollection) return;
    if (inCollection) {
      setPendingRemove(true);
      return;
    }
    onAddToCollection(card);
  };

  const confirmRemove = () => {
    setPendingRemove(false);
    if (onAddToCollection) onAddToCollection(card);
  };

  // A swap, not a merge: the refetched document is authoritative and complete.
  //
  // That is only safe while the grid row and the detail document resolve the
  // SAME price. They didn't (P1-47) — the grid was projected down to
  // `cardmarket.prices.avg30` while this route returned the whole block, so the
  // figure below visibly changed inside an already-open sheet, with no user
  // action, the moment the fetch landed. The fix is server-side: the price
  // fields now travel with the grid row (CARD_PRICE_FIELDS). Narrow that
  // projection again and this line will start moving the price again.
  const full = detail || card;
  const rarity = getRarityBadge(full);
  const priceDisplay = getPriceDisplay(full, condition, formatAmount);
  // Where the figure comes from and how old it is — the price is a snapshot,
  // not live data, and the age belongs next to the number (#64). The display
  // object is passed in so the line names the source of the price ACTUALLY
  // SHOWN: a provider quote rendered under "Cardmarket price (EUR)" would be a
  // fresh instance of the bug #64 exists to kill, on the one figure the sheet
  // marks as not an estimate.
  const provenance = priceDisplay.hasPrice
    ? priceProvenance(full, priceDisplay)
    : null;

  const stockImg = full?.images?.large || full?.images?.small || PLACEHOLDER_IMG;
  const hasOwnerPhotos = ownerUrls.length > 0;
  const activeOwnerUrl = hasOwnerPhotos ? ownerUrls[activePhotoIdx] || ownerUrls[0] : null;
  const imgSrc = activeOwnerUrl || stockImg;

  // Whether the picture on screen actually IS the owner's photograph — which is
  // not the same question as whether they have one (#252). When the photo 404s,
  // CardImage substitutes this card's stock art, and every claim of provenance
  // below is a claim about the image that is displayed, so it has to be gated
  // on this and not on `hasOwnerPhotos`.
  const showingOwnerPhoto = hasOwnerPhotos && !primaryImageFellBack;

  // Foil is a property of the printed card, so it belongs on catalogue art. An
  // owner's own photograph is evidence about their specific copy — laying a
  // synthetic shimmer over it would dress up the very thing the photo exists to
  // show honestly, which is the same reason condition photos are labelled a
  // claim rather than a verification.
  const foil = isFoilRarity(full) && !hasOwnerPhotos;

  const attacks = full?.attacks || [];
  const weaknesses = full?.weaknesses || [];
  const resistances = full?.resistances || [];
  const setLine = [full?.set?.name, full?.number && `#${full.number}`]
    .filter(Boolean)
    .join(" · ");

  // Only claim "no description" once the fetch has actually settled.
  const settled = !loading;
  const hasBody = attacks.length > 0 || !!full?.flavorText;

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      // Named by its own title (E2E audit 7·17 / 8·1): without a DialogTitle,
      // MUI's aria-labelledby points at nothing and a screen reader says "dialog".
      scroll="paper"
      PaperProps={{ 'aria-label': card?.name || 'Card',
        "data-testid": "card-detail-modal",
        sx: { borderRadius: 4, overflow: "hidden", m: 1.5 },
      }}
    >
      <Box sx={{ position: "relative", bgcolor: "#0f172a" }}>
        <IconButton
          onClick={onClose}
          aria-label="Close card details"
          // 44 (the touch rule): size="small" measured ~30dp on device (T-25).
          sx={{
            position: "absolute", top: 8, right: 8, zIndex: 2,
            width: 44, height: 44,
            bgcolor: "rgba(15,23,42,0.55)", color: "#fff",
            "&:hover": { bgcolor: "rgba(15,23,42,0.75)" },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <Box sx={{ p: 2, bgcolor: "#0f172a", height: "52vh" }}>
          <HoloCard active={foil} radius={10}>
            <CardImage
              src={imgSrc}
              alt={full?.name || "Pokemon card"}
              data-testid="card-detail-primary-image"
              radius={10}
              style={{ objectFit: "contain" }}
              // An owner photo is hosted somewhere else entirely, so it can 404
              // on its own while the catalogue art is fine. When it does, the
              // honest substitute is THIS card's official art — which we have
              // in `stockImg` — not CardImage's global last-resort image. With
              // no owner photo `src` already IS `stockImg`, so leave that path
              // on CardImage's own default rather than pointing it at itself.
              fallbackSrc={hasOwnerPhotos ? stockImg : undefined}
              // The swap above is silent by design — the sheet keeps showing a
              // picture. This is how the badge below finds out that the picture
              // is no longer the one it is describing.
              onFallbackChange={setPrimaryImageFellBack}
            />
          </HoloCard>
        </Box>
        {showingOwnerPhoto && (
          <Box
            data-testid="card-detail-owner-badge"
            sx={{
              position: "absolute", left: 12, top: 12,
              bgcolor: "rgba(15,23,42,0.72)", color: "#e2e8f0",
              px: 1.2, py: 0.3, borderRadius: 999,
              fontSize: 10, fontWeight: 800,
              textTransform: "uppercase", letterSpacing: "0.06em",
            }}
          >
            Your photo
          </Box>
        )}
      </Box>

      {/* Gated on `hasOwnerPhotos`, NOT on `showingOwnerPhoto`, and that
          difference is deliberate. The badge is an assertion about the picture
          on screen and has to go when that picture changes underneath it; the
          strip is the way BACK to a shot that does load, and pulling it on the
          first dead URL would strand the owner on stock art with their working
          photographs unreachable. Each thumbnail still asserts only about
          itself, and a dead one fails visibly in place. */}
      {hasOwnerPhotos && ownerUrls.length > 1 && (
        <Box
          data-testid="card-detail-owner-strip"
          sx={{
            display: "flex", gap: 0.8, px: 1.5, py: 1,
            overflowX: "auto", bgcolor: "#0f172a",
            borderTop: "1px solid rgba(255,255,255,0.08)",
          }}
        >
          {ownerUrls.map((url, i) => (
            <Box
              key={url}
              component="img"
              src={url}
              alt={`Your photo ${i + 1}`}
              onClick={() => setActivePhotoIdx(i)}
              sx={{
                width: 52, height: 52, objectFit: "cover",
                borderRadius: 1.5, cursor: "pointer", flexShrink: 0,
                border: i === activePhotoIdx
                  ? "2px solid #a78bfa"
                  : "2px solid transparent",
                opacity: i === activePhotoIdx ? 1 : 0.7,
                transition: "opacity 0.15s, border-color 0.15s",
              }}
            />
          ))}
        </Box>
      )}

      <Box sx={{ p: 2.5, overflowY: "auto" }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 20, fontWeight: 900, color: "#0f172a", lineHeight: 1.2 }}>
              {full?.name || "Unknown card"}
            </Typography>
            {setLine && (
              <Typography sx={{ fontSize: 13, color: "#64748b", mt: 0.3 }}>
                {setLine}
              </Typography>
            )}
          </Box>
          {/* Tapping the price opens what it has actually been doing — the
              question a trader asks before agreeing to a swap. */}
          <Box
            component={priceDisplay.hasPrice ? "button" : "div"}
            type={priceDisplay.hasPrice ? "button" : undefined}
            onClick={priceDisplay.hasPrice ? () => setHistoryOpen((v) => !v) : undefined}
            aria-expanded={priceDisplay.hasPrice ? historyOpen : undefined}
            data-testid={priceDisplay.hasPrice ? "card-detail-price-button" : undefined}
            sx={{
              textAlign: "right",
              flexShrink: 0,
              background: "none",
              border: "none",
              p: 0,
              cursor: priceDisplay.hasPrice ? "pointer" : "default",
              font: "inherit",
            }}
          >
            {priceDisplay.qualifier && (
              <Typography sx={{
                fontSize: 10, fontWeight: 800, color: "#64748b",
                textTransform: "uppercase", letterSpacing: "0.05em",
              }}>
                {priceDisplay.qualifier}
              </Typography>
            )}
            <Typography
              data-testid="card-detail-price"
              sx={{
                fontSize: priceDisplay.hasPrice ? 20 : 12,
                fontWeight: priceDisplay.hasPrice ? 900 : 700,
                color: priceDisplay.hasPrice ? "#6b21a8" : "#64748b",
              }}
            >
              {priceDisplay.hasPrice ? priceDisplay.amount : priceDisplay.label}
            </Typography>
            {provenance && (
              <Typography
                data-testid="card-detail-price-provenance"
                sx={{ fontSize: 10, color: "#64748b" }}
              >
                {provenance}
              </Typography>
            )}
            {priceDisplay.hasPrice && (
              <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#6b21a8" }}>
                {historyOpen ? "Hide history" : "View history"}
              </Typography>
            )}
          </Box>
        </Box>

        {historyOpen && priceDisplay.hasPrice && (
          <Box sx={{ mt: 1.5, border: "1px solid rgba(107,33,168,0.12)", borderRadius: 2 }}>
            {/* Same grade the headline above was priced at. The chart forwards
                this to the history route, which filters on condition exactly
                ("one condition, because mixing grades on a line would be
                meaningless"), so without it the expansion drew the Near Mint
                series under a Damaged headline — the same two-prices-one-card
                defect this entry closes, one level down. Undefined on the
                catalog path, where the chart's own near_mint default applies. */}
            <PriceHistoryChart cardId={full?._id} condition={condition} />
          </Box>
        )}

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.7, mt: 1.5 }}>
          {rarity && (
            <Chip
              size="small"
              label={rarity.label}
              data-testid="card-detail-rarity"
              sx={{
                bgcolor: rarity.color, color: "#fff",
                fontWeight: 800, fontSize: 11, height: 24,
              }}
            />
          )}
          {full?.supertype && (
            <Chip size="small" label={full.supertype} variant="outlined" sx={{ fontSize: 11, height: 24 }} />
          )}
          {(full?.subtypes || []).map((s) => (
            <Chip key={s} size="small" label={s} variant="outlined" sx={{ fontSize: 11, height: 24 }} />
          ))}
          {(full?.types || []).map((t) => (
            <Chip key={t} size="small" label={t} variant="outlined" sx={{ fontSize: 11, height: 24 }} />
          ))}
          {full?.hp && (
            <Chip size="small" label={`HP ${full.hp}`} variant="outlined" sx={{ fontSize: 11, height: 24 }} />
          )}
        </Box>

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <Loader context="panel" label="Loading card details" />
          </Box>
        )}

        {settled && attacks.length > 0 && (
          <Section title="Attacks">
            {attacks.map((atk, i) => (
              <Box key={`${atk.name}-${i}`} sx={{ mb: 1.2 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 800, color: "#0f172a" }}>
                    {atk.name}
                    {atk.cost?.length ? (
                      <Typography component="span" sx={{ fontSize: 12, color: "#64748b", fontWeight: 600, ml: 0.8 }}>
                        {atk.cost.join(" · ")}
                      </Typography>
                    ) : null}
                  </Typography>
                  {atk.damage && (
                    <Typography sx={{ fontSize: 14, fontWeight: 900, color: "#6b21a8", flexShrink: 0 }}>
                      {atk.damage}
                    </Typography>
                  )}
                </Box>
                {atk.text && (
                  <Typography sx={{ fontSize: 13, color: "#475569", lineHeight: 1.5, mt: 0.3 }}>
                    {atk.text}
                  </Typography>
                )}
              </Box>
            ))}
          </Section>
        )}

        {settled && (weaknesses.length > 0 || resistances.length > 0 || full?.retreatCost?.length > 0) && (
          <Section title="Battle stats">
            {weaknesses.length > 0 && (
              <MetaRow label="Weakness" value={weaknesses.map((w) => `${w.type} ${w.value || ""}`.trim()).join(", ")} />
            )}
            {resistances.length > 0 && (
              <MetaRow label="Resistance" value={resistances.map((r) => `${r.type} ${r.value || ""}`.trim()).join(", ")} />
            )}
            {full?.retreatCost?.length > 0 && (
              <MetaRow label="Retreat cost" value={String(full.retreatCost.length)} />
            )}
          </Section>
        )}

        {settled && full?.flavorText && (
          <Section title="Flavor text">
            <Typography sx={{ fontSize: 13, color: "#475569", lineHeight: 1.6, fontStyle: "italic" }}>
              {full.flavorText}
            </Typography>
          </Section>
        )}

        {settled && full?.artist && (
          <Section title="Illustrator">
            <Typography sx={{ fontSize: 13, color: "#475569" }}>{full.artist}</Typography>
          </Section>
        )}

        {settled && !hasBody && (
          <Typography
            data-testid="card-detail-empty"
            sx={{ fontSize: 13, color: "#64748b", mt: 2, lineHeight: 1.5 }}
          >
            {loadError
              ? "Couldn't load the full card details. Check your connection and try again."
              : "This card has no attack or flavour text on file."}
          </Typography>
        )}

        {(onToggleWishlist || onAddToCollection) && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: "flex", gap: 1 }}>
              {onToggleWishlist && (
                <Button
                  fullWidth
                  variant="outlined"
                  data-testid="card-detail-wishlist"
                  onClick={() => onToggleWishlist(card._id, card.name)}
                  startIcon={isWishlisted ? <FavoriteIcon /> : <FavoriteBorderIcon />}
                  sx={{
                    borderRadius: 3, textTransform: "none", fontWeight: 800,
                    color: isWishlisted ? "#ef4444" : "#6b21a8",
                    borderColor: isWishlisted ? "#ef4444" : "rgba(107,33,168,0.3)",
                  }}
                >
                  {isWishlisted ? "Wishlisted" : "Wishlist"}
                </Button>
              )}
              {onAddToCollection && (
                <Button
                  fullWidth
                  variant="contained"
                  data-testid="card-detail-add"
                  onClick={handleAddToCollection}
                  aria-label={inCollection ? "Remove from collection" : "Add to collection"}
                  startIcon={inCollection ? <CheckIcon /> : <AddIcon />}
                  sx={{
                    borderRadius: 3, textTransform: "none", fontWeight: 800,
                    bgcolor: inCollection ? "#10b981" : "#6b21a8",
                    "&:hover": { bgcolor: inCollection ? "#059669" : "#581c87" },
                  }}
                >
                  {inCollection ? "In collection" : "Add"}
                </Button>
              )}
            </Box>
          </>
        )}
      </Box>
    </Dialog>
    {open && pendingRemove && (
      <RemoveCardDialog
        idPrefix="card-detail-remove"
        promised={promised}
        onCancel={() => setPendingRemove(false)}
        onConfirm={confirmRemove}
      />
    )}
    </>
  );
};

export default CardDetailModal;
